import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"

import type { Lifetime } from "@/lib/Lifetime.ts"
import type { IRemoteLogModel } from "@/routes/tasks/TaskViewScene/RemoteLogModel.ts"
import type { FitAddon } from "@xterm/addon-fit"
import type { ITheme, Terminal } from "@xterm/xterm"
import type { Subscription } from "rxjs"

const log = getLogger("TerminalModel")

export class TerminalModel extends Model {
  constructor(
    readonly lifetime: Lifetime,
    readonly text: string | null,
    readonly logModel: IRemoteLogModel | null
  ) {
    super("TerminalModel")
  }

  private element: HTMLElement | null = null
  private terminal: Terminal | null = null
  private fitAddon: FitAddon | null = null

  private subscription: Subscription | null = null

  private createLightTheme = (overrides: Partial<ITheme> = {}): ITheme => ({
    foreground: "#000000",
    background: "#f6f6f6",
    cursor: "#000000",
    cursorAccent: "#ffffff",
    selectionBackground: "#0078d4",
    selectionForeground: "#ffffff",
    black: "#000000",
    red: "#cd3131",
    green: "#00bc00",
    yellow: "#949800",
    blue: "#0451a5",
    magenta: "#bc05bc",
    cyan: "#0598bc",
    white: "#555555",
    brightBlack: "#666666",
    brightRed: "#cd3131",
    brightGreen: "#14ce14",
    brightYellow: "#b5ba00",
    brightBlue: "#0451a5",
    brightMagenta: "#bc05bc",
    brightCyan: "#0598bc",
    brightWhite: "#a5a5a5",
    ...overrides,
  })

  async init(element: HTMLElement, useDarkTheme: boolean, backgroundColor?: string) {
    const [{ Terminal }, { FitAddon }, { WebglAddon }] = await Promise.all([
      import("@xterm/xterm"),
      import("@xterm/addon-fit"),
      import("@xterm/addon-webgl"),
      import("@xterm/xterm/css/xterm.css")
    ])

    if (this.terminal && element === this.element) {
      log.error("Terminal is already initialized for this element")
      return
    }

    this.element = element

    this.fitAddon = new FitAddon()
    this.terminal = new Terminal({
      fontSize: 13,
      fontFamily: "JetBrains Mono Variable, Courier New, Courier, monospace",
      theme: useDarkTheme ? undefined : this.createLightTheme(backgroundColor ? { background: backgroundColor } : {}),
      convertEol: false // Ensure carriage returns (\r) work properly for progress bars
    })

    this.terminal.loadAddon(this.fitAddon)
    this.terminal.loadAddon(new WebglAddon())
    this.terminal.open(element)
    this.fitAddon.fit()

    const resizeObserver = new ResizeObserver(() => this.fitAddon?.fit())

    resizeObserver.observe(element)

    this.lifetime.add(() => resizeObserver.disconnect(), "TerminalModel:resizeObserver")

    if (this.text) this.terminal.write(this.text)
    if (this.logModel) this.doSubscribe()
  }

  private doSubscribe() {
    this.subscription = this.logModel?.getLogs().subscribe(line => this.terminal?.write(line)) || null
    if (this.subscription) this.lifetime.add(this.subscription, "TerminalModel:doSubscribe")
  }

  terminate() {
    super.terminate()

    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }

    if (this.terminal) {
      this.terminal.dispose()
      this.terminal = null
    }

    this.fitAddon = null
  }

  getLastLinesOrSelection(num: number) {
    if (!this.terminal) return ""
    const lineNum = this.terminal.buffer.active.length

    if (!this.terminal.hasSelection()) {
      this.terminal.selectLines(lineNum - num, lineNum - 1)
    }

    const portion = this.terminal.getSelection()
    this.terminal.clearSelection()
    return portion
  }
}